import { Link } from "react-router-dom";
import { useMe } from "../../lib/me";
import { PageHeader } from "../../components/PageHeader";
import { SourceHealthTable } from "../jobs/SourceHealth";
import { Section } from "../lib/ui";
import { oneOf, useUrlState } from "../lib/url";
import { AccountsTab } from "./AccountsTab";
import { ArtistsTab } from "./ArtistsTab";
import { LexiconTab } from "./LexiconTab";
import { SettingsTab } from "./SettingsTab";
import { TeamTab } from "./TeamTab";
import { TokensTab } from "./TokensTab";

export const TABS = ["artists", "lexicon", "accounts", "tokens", "team", "settings", "sources"] as const;
export type Tab = (typeof TABS)[number];
const TITLES: Record<Tab, string> = {
  artists: "Artists", lexicon: "Lexicon", accounts: "Accounts", tokens: "Tokens", team: "Team", settings: "All settings",
  sources: "Sources",
};
/** Tabs whose reads need the `admin` scope: hidden from everyone else. The other tabs are readable by anyone and
 * show their forms only to admins (every write there needs `admin`; Scan now and the Fall version need `make`). */
const ADMIN_ONLY: readonly Tab[] = ["tokens", "team"];

export function SettingsView() {
  const { sp } = useUrlState();
  const tab = oneOf(sp.get("tab"), TABS, "artists");
  const me = useMe();
  const isAdmin = !!me.data?.scopes.includes("admin");
  const visible = TABS.filter((t) => isAdmin || !ADMIN_ONLY.includes(t) || t === tab);
  return (
    <div className="ws7-page" data-testid="settings-page">
      <PageHeader title="Settings" />
      <nav className="ws7-tabs" aria-label="Settings sections">
        {visible.map((t) => (
          <Link key={t} to={{ search: `?tab=${t}` }} aria-current={t === tab ? "page" : undefined} data-testid={`tab-${t}`}>
            {TITLES[t]}
          </Link>
        ))}
      </nav>
      <div data-testid={`tab-panel-${tab}`}>
        {tab === "artists" && <ArtistsTab admin={isAdmin} />}
        {tab === "lexicon" && <LexiconTab admin={isAdmin} />}
        {tab === "accounts" && <AccountsTab admin={isAdmin} />}
        {tab === "tokens" && (isAdmin ? <TokensTab /> : (
          <p className="ws7-muted" data-testid="tokens-admin-only">{me.isPending ? "Loading…" : "Tokens are managed by admins."}</p>
        ))}
        {tab === "team" && (isAdmin ? <TeamTab /> : (
          <p className="ws7-muted" data-testid="team-admin-only">{me.isPending ? "Loading…" : "The team is managed by admins."}</p>
        ))}
        {tab === "settings" && <SettingsTab admin={isAdmin} />}
        {tab === "sources" && (
          <Section id="sources" title="Source health and kill switches">
            <SourceHealthTable canSwitch={isAdmin} />
          </Section>
        )}
      </div>
    </div>
  );
}
